import moment from 'moment';
import { BUS_DOC_FIELDS, BUS_STATUS_OPTIONS, expiryState, orderStops } from '../transportUtils';

// Pure builders for the Transport reports: each turns the data the Transport page already holds into
// { columns, rows, summary }. Rows hold plain text (so the same rows feed the table and the Excel export);
// `tones` marks a cell 'bad' (red) or 'warn' (orange). Kept free of React so the mobile app can reuse them later.

const dash = value => (value === undefined || value === null || value === '' ? '--' : value);
const formatDate = value => (value ? moment(value).format('DD MMM YYYY') : '--');
const statusLabel = value => BUS_STATUS_OPTIONS.find(o => o.value === value)?.label || dash(value);

// Date + how close it is to lapsing, e.g. "12 Mar 2027" / "03 Sep 2026 (expired)" / "20 Oct 2026 (due in 8d)".
const docCell = date => {
  if (!date) return { text: '--', tone: null };
  const state = expiryState(date);
  if (!state) return { text: formatDate(date), tone: null };
  return state.state === 'expired'
    ? { text: `${formatDate(date)} (expired)`, tone: 'bad' }
    : { text: `${formatDate(date)} (due in ${state.days}d)`, tone: 'warn' };
};

export const buildRouteReport = ({ routes, areasByRoute, studentsByRoute, assignments, busesById, driversById }) => {
  const rows = routes.map(route => {
    const id = route.transportFeeStructId;
    const stops = orderStops(areasByRoute[id]);
    const students = (studentsByRoute[id] || []).length;
    const routeAssignments = assignments.filter(a => a.transportFeeStructId === id);
    const seats = routeAssignments.reduce((sum, a) => sum + (busesById[a.busId]?.seatingCapacity || 0), 0);
    const mismatched = stops.filter(stop => stop.ruteName !== route.description).length;
    const issues = [];
    // the same issues with their tone, for the route cards (the text joined below is what the Excel export carries)
    const issueList = [];
    const tones = {};
    if (!routeAssignments.length) {
      issues.push('No bus & driver');
      issueList.push({ text: 'No bus & driver', tone: 'warn' });
      tones.issues = 'warn';
    }
    if (seats > 0 && students > seats) {
      issues.push(`Over capacity (${students}/${seats})`);
      issueList.push({ text: `Over capacity (${students}/${seats})`, tone: 'bad' });
      tones.issues = 'bad';
      tones.students = 'bad';
    }
    const busIds = routeAssignments.map(a => a.busId);
    if (busIds.length !== new Set(busIds).size) {
      issues.push('Same bus on this route with two drivers');
      issueList.push({ text: 'Same bus on this route with two drivers', tone: 'warn' });
      tones.issues = tones.issues || 'warn';
    }
    if (!stops.length) {
      issues.push('No stops');
      issueList.push({ text: 'No stops', tone: 'warn' });
    }
    if (!students) {
      issues.push('No students');
      issueList.push({ text: 'No students', tone: 'warn' });
    }
    if (mismatched) {
      issues.push(`${mismatched} stop(s) under another route name`);
      issueList.push({ text: `${mismatched} stop(s) under another route name`, tone: 'warn' });
      tones.issues = tones.issues || 'warn';
    }
    return {
      route: route.description,
      stopCount: stops.length,
      stops: stops.map(stop => stop.areaName).join(' > ') || '--',
      // the same stops with their distances, for the table to draw (the text above is what the Excel export carries)
      stopList: stops.map(stop => ({ name: stop.areaName, distance: stop.distance })),
      students,
      bus: routeAssignments.map(a => dash(busesById[a.busId]?.busNumber)).join(', ') || '--',
      driver: routeAssignments.map(a => dash(driversById[a.employeeId]?.name)).join(', ') || '--',
      phone: routeAssignments.map(a => dash(driversById[a.employeeId]?.phoneNumber)).join(', ') || '--',
      seats: seats || '--',
      issues: issues.join('; ') || 'OK',
      issueList,
      // each bus on the route with its driver, for the route cards
      assignmentList: routeAssignments.map(a => ({
        bus: dash(busesById[a.busId]?.busNumber),
        driver: dash(driversById[a.employeeId]?.name),
        phone: driversById[a.employeeId]?.phoneNumber || '',
      })),
      seatCount: seats,
      tones,
    };
  });
  return {
    columns: [
      { key: 'route', label: 'Route' },
      { key: 'stopCount', label: 'Stops' },
      { key: 'stops', label: 'Stop order' },
      { key: 'students', label: 'Students' },
      { key: 'bus', label: 'Bus' },
      { key: 'driver', label: 'Driver' },
      { key: 'phone', label: 'Driver phone' },
      { key: 'seats', label: 'Seats' },
      { key: 'issues', label: 'Attention' },
    ],
    rows,
    summary: [
      { label: 'Routes', value: rows.length },
      { label: 'Students on routes', value: rows.reduce((sum, r) => sum + r.students, 0) },
      { label: 'Without bus & driver', value: rows.filter(r => r.bus === '--').length, tone: 'warn' },
      { label: 'Over capacity', value: rows.filter(r => r.tones.students === 'bad').length, tone: 'bad' },
    ],
  };
};

export const buildBusReport = ({ buses, assignments, routesById, driversById }) => {
  const rows = buses.map(bus => {
    const busAssignments = assignments.filter(a => a.busId === bus.busId);
    const tones = {};
    const docs = {};
    BUS_DOC_FIELDS.forEach(({ key }) => {
      const cell = docCell(bus[key]);
      docs[key] = cell.text;
      if (cell.tone) tones[key] = cell.tone;
    });
    if (bus.status !== 'ACTIVE') tones.status = 'warn';
    return {
      bus: bus.busNumber,
      registration: dash(bus.registrationNumber),
      vehicle: [bus.make, bus.model, bus.manufactureYear].filter(Boolean).join(' ') || '--',
      seats: dash(bus.seatingCapacity),
      fuel: dash(bus.fuelType),
      status: statusLabel(bus.status),
      route:
        busAssignments.map(a => routesById[a.transportFeeStructId]?.description || a.routeName).join(', ') ||
        'Unassigned',
      driver: busAssignments.map(a => dash(driversById[a.employeeId]?.name)).join(', ') || '--',
      ...docs,
      tones,
    };
  });
  const lapsing = rows.filter(r => BUS_DOC_FIELDS.some(({ key }) => r.tones[key])).length;
  return {
    columns: [
      { key: 'bus', label: 'Bus' },
      { key: 'registration', label: 'Registration' },
      { key: 'vehicle', label: 'Vehicle' },
      { key: 'seats', label: 'Seats' },
      { key: 'fuel', label: 'Fuel' },
      { key: 'status', label: 'Status' },
      { key: 'route', label: 'Route' },
      { key: 'driver', label: 'Driver' },
      ...BUS_DOC_FIELDS.map(({ key, label }) => ({ key, label: `${label} expiry` })),
    ],
    rows,
    summary: [
      { label: 'Buses', value: rows.length },
      { label: 'Active', value: buses.filter(b => b.status === 'ACTIVE').length },
      { label: 'Not assigned to a route', value: rows.filter(r => r.route === 'Unassigned').length, tone: 'warn' },
      { label: 'Documents expired / due', value: lapsing, tone: 'bad' },
    ],
  };
};

export const buildDriverReport = ({ drivers, assignments, busesById, routesById }) => {
  const rows = drivers.map(driver => {
    const driverAssignments = assignments.filter(a => a.employeeId === driver.employeeId);
    return {
      driver: dash(driver.name),
      phone: dash(driver.phoneNumber),
      email: dash(driver.email),
      experience: dash(driver.totalExperience),
      status: dash(driver.status),
      bus: driverAssignments.map(a => dash(busesById[a.busId]?.busNumber)).join(', ') || '--',
      route:
        driverAssignments.map(a => routesById[a.transportFeeStructId]?.description || a.routeName).join(', ') ||
        'Unassigned',
      tones: driverAssignments.length ? {} : { route: 'warn' },
    };
  });
  return {
    columns: [
      { key: 'driver', label: 'Driver' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'experience', label: 'Experience' },
      { key: 'status', label: 'Status' },
      { key: 'bus', label: 'Bus' },
      { key: 'route', label: 'Route' },
    ],
    rows,
    summary: [
      { label: 'Drivers', value: rows.length },
      { label: 'Assigned to a route', value: rows.filter(r => r.route !== 'Unassigned').length },
      { label: 'Not assigned', value: rows.filter(r => r.route === 'Unassigned').length, tone: 'warn' },
    ],
  };
};

const ATTENDANCE_STATUS = { PICKED: 'Picked up', DROPPED: 'Dropped', ABSENT: 'Absent' };
const TRIP = { PICKUP: 'Pickup', DROP: 'Drop' };

// Bus attendance records (BusAttendanceReportResponse) -> report rows. busesById / driversById fill in the
// bus number and driver name, which the backend returns only as ids.
export const buildAttendanceReport = ({ records, busesById, driversById }) => {
  const rows = records
    .slice()
    .sort(
      (a, b) =>
        `${b.attendanceDate}`.localeCompare(`${a.attendanceDate}`) || `${a.routeName}`.localeCompare(`${b.routeName}`)
    )
    .map(record => ({
      date: formatDate(record.attendanceDate),
      student: dash(record.studentName),
      course: dash(record.courseName),
      route: dash(record.routeName),
      bus: dash(busesById[record.busId]?.busNumber),
      driver: dash(driversById[record.employeeId]?.name),
      trip: TRIP[record.tripType] || dash(record.tripType),
      status: ATTENDANCE_STATUS[record.status] || dash(record.status),
      markedAt: record.markedDateTime ? moment(record.markedDateTime).format('hh:mm A') : '--',
      remarks: dash(record.remarks),
      // raw values for filtering
      raw: { routeName: record.routeName, tripType: record.tripType, status: record.status },
      tones: record.status === 'ABSENT' ? { status: 'bad' } : {},
    }));
  return {
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'student', label: 'Student' },
      { key: 'course', label: 'Class' },
      { key: 'route', label: 'Route' },
      { key: 'bus', label: 'Bus' },
      { key: 'driver', label: 'Driver' },
      { key: 'trip', label: 'Trip' },
      { key: 'status', label: 'Status' },
      { key: 'markedAt', label: 'Marked at' },
      { key: 'remarks', label: 'Remarks' },
    ],
    rows,
  };
};
